import React from 'react';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * ThemeToggle — canonical light/dark switcher.
 *
 * Feb 2026 iter #124: single reusable component so every shell surface
 * (PublicNavbar, DashboardFocusShell, SuiteNav, page-level nav bars)
 * ships the same button, keyboard-focus ring, and screen-reader label.
 * The `ThemeProvider` at App.js already persists the choice to
 * localStorage and drives the `.dark` class on <html> — this component
 * is a pure controller for `toggleTheme` from that context.
 *
 * Variants
 * --------
 * • `variant="ghost"` (default) — subtle icon-only pill for shells.
 * • `variant="solid"` — higher-contrast for hero/marketing surfaces.
 *
 * Usage
 * -----
 *   <ThemeToggle />                             // default
 *   <ThemeToggle variant="solid" />             // hero
 *   <ThemeToggle className="ml-2" />            // extra tailwind
 */
export const ThemeToggle = ({ variant = 'ghost', className = '' }) => {
  const { theme, toggleTheme } = useTheme();
  const dark = theme === 'dark';

  const base =
    'inline-flex items-center justify-center rounded-full transition-colors ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-cn-primary ' +
    'focus-visible:ring-offset-2 focus-visible:ring-offset-cn-bg';

  const variants = {
    ghost:
      'w-9 h-9 border border-cn-border/60 bg-white/60 dark:bg-white/5 ' +
      'hover:bg-white dark:hover:bg-white/10 text-cn-text-muted',
    solid:
      'w-10 h-10 border border-cn-border bg-cn-bg-elev hover:bg-cn-bone-100 ' +
      'dark:hover:bg-cn-ink-800 text-cn-text',
  };

  return (
    <button
      type="button"
      onClick={toggleTheme}
      className={`${base} ${variants[variant] || variants.ghost} ${className}`}
      aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      title={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      data-testid="theme-toggle"
      data-theme-state={theme}
    >
      {dark ? (
        <Sun className="w-4 h-4 text-yellow-400" aria-hidden="true" />
      ) : (
        <Moon className="w-4 h-4" aria-hidden="true" />
      )}
    </button>
  );
};

export default ThemeToggle;
